/**
 * 磁性套索（套索工具"模式"选项新增的第三种，DESIGN.md 对比 Photoshop
 * 补全）。核心追踪算法在 engine/path/magnetic-lasso.ts（纯函数，只认
 * SourceImage + 两个点，不碰 DOM）；这里只管手势状态机，手感照真实
 * Photoshop：鼠标不用按住，移动到哪，路径就沿着最近一个"紧固点"附近的
 * 边缘吸附延伸到哪；点一下强制落一个紧固点；"频率"选项 > 0 时，追踪
 * 段够长会自动落点；双击/回车闭合成选区（复用既有的 lassoMask/
 * fromPolygon，这个模块不管栅格化）；Esc 取消整个手势。
 *
 * 取样源跟魔棒/快速选择共用同一套 `magicWandSampleSource`：手势开始时
 * 取一次快照，整个手势期间沿用——文档在套索拖拽过程中不会变，没必要
 * 每次 move 都重新合成一遍（quick-select.ts 反而是每次 move 都重新取样，
 * 那是因为它的取样点在变、被选中的图层内容也可能在同一手势内已经改了；
 * 磁性套索只是在同一份图像上找路径，没有这个理由）。
 */
import { pathLength, traceMagneticPath, type Point } from '../../engine/path/magnetic-lasso';
import type { SourceImage } from '../../engine/selection/magic-wand';
import { magicWandSampleSource } from './magic-wand-tool';
import { applyCombine, combineFromModifiers, lassoMask, type SelectionCombine } from './marquee';
import type { AppStore } from '../../app/store';
import type { ToolManager } from './registry';

export interface MagneticLassoDeps {
  readonly store: AppStore;
  readonly tools: ToolManager;
  readonly requestRender: () => void;
}

/** 点回起点多少 px（文档坐标）内视为"落在起点上"，闭合手势——跟
 * PolygonGesture 的闭合半径（4px）保持一致的手感。 */
const CLOSE_RADIUS = 4;

export class MagneticLassoToolController {
  readonly #deps: MagneticLassoDeps;
  #image: SourceImage | null = null;
  /** 已落下的紧固点（覆盖层画方块标记用，跟多边形套索的顶点标记区分手感一致）。 */
  #anchors: Point[] = [];
  /** 已提交的稠密路径顶点（含各段追踪出的中间点），最终闭合时就是它。 */
  #committed: Point[] = [];
  /** 从最后一个紧固点到当前指针位置的实时追踪预览，还没提交。 */
  #liveTrace: readonly Point[] = [];
  #combine: SelectionCombine = 'replace';

  constructor(deps: MagneticLassoDeps) {
    this.#deps = deps;
  }

  get active(): boolean {
    return this.#image !== null;
  }

  /** 已提交路径 + 当前实时预览段——覆盖层画线用。 */
  previewPath(): readonly Point[] {
    return this.#liveTrace.length > 0 ? [...this.#committed, ...this.#liveTrace.slice(1)] : this.#committed;
  }

  /** 已落下的紧固点——覆盖层画方块标记用。 */
  anchorPoints(): readonly Point[] {
    return this.#anchors;
  }

  handlePointer(
    phase: 'down' | 'move' | 'up' | 'double',
    point: { x: number; y: number },
    modifiers: { readonly shiftKey: boolean; readonly altKey: boolean },
  ): boolean {
    const { store, tools } = this.#deps;
    if (tools.active.id !== 'lasso' || tools.getOption('lasso', 'mode') !== 'magnetic') {
      // 手势中途切工具/切模式：半成品直接丢弃，不留下没画完的路径
      if (this.active) this.#reset();
      return false;
    }
    const document = store.document;
    if (document === null) return true;

    if (phase === 'down') {
      if (!this.active) {
        // 简化（已披露）：套索属性栏没有魔棒/快速选择那样的"取样所有图层"
        // 开关，磁性套索固定只认当前图层（跟这两个工具的默认值一致）——
        // 真实 Photoshop 磁性套索本来就是直接在画布可见内容上找边缘，
        // 没有这个开关这件事本身也不是本工具独有的缺口
        this.#image = magicWandSampleSource(store, document, false);
        this.#combine = combineFromModifiers(modifiers, tools.getOption('lasso', 'combine') as SelectionCombine);
        this.#anchors = [point];
        this.#committed = [point];
        this.#liveTrace = [];
      } else {
        this.#clickAt(point);
      }
      this.#deps.requestRender();
      return true;
    }
    if (phase === 'move') {
      if (this.active) this.#traceTo(point);
      return true;
    }
    if (phase === 'double') {
      this.#close();
      return true;
    }
    // 'up' 对这个工具没有意义：落点由 down（紧固点）/move（追踪）决定，
    // 不是"按住拖拽"的手势，跟自由套索/多边形套索的 up 语义不一样
    return true;
  }

  /** Enter：跟双击等价，闭合当前路径。 */
  commit(): void {
    this.#close();
  }

  /** Esc：整个手势作废，不留下任何选区改动。 */
  cancel(): void {
    this.#reset();
    this.#deps.requestRender();
  }

  #traceTo(point: Point): void {
    const image = this.#image;
    const from = this.#committed.at(-1);
    if (image === null || from === undefined) return;
    const { tools } = this.#deps;
    const searchRadius = tools.getOption('lasso', 'magneticWidth') as number;
    const contrastThreshold = (tools.getOption('lasso', 'magneticContrast') as number) / 100;
    const traced = traceMagneticPath(image, from, point, { searchRadius, contrastThreshold });
    this.#liveTrace = traced;

    const frequency = tools.getOption('lasso', 'magneticFrequency') as number;
    // 频率：追踪段够长（近似，不是沿路径精确等距切分）就自动落一个紧固点，
    // 省得手动点个不停——真实 Photoshop 应该也是类似的近似节流，没有
    // 公开精确算法
    if (frequency > 0 && pathLength(traced) >= frequency) {
      this.#commitTrace(point);
    }
    this.#deps.requestRender();
  }

  #clickAt(point: Point): void {
    const first = this.#anchors[0];
    if (first !== undefined && this.#anchors.length >= 2 && Math.hypot(point.x - first.x, point.y - first.y) <= CLOSE_RADIUS) {
      this.#close();
      return;
    }
    this.#commitTrace(point);
  }

  /** 把当前 liveTrace 并入 committed，末尾记一个紧固点。 */
  #commitTrace(point: Point): void {
    if (this.#liveTrace.length > 0) {
      this.#committed.push(...this.#liveTrace.slice(1));
    } else {
      this.#committed.push(point);
    }
    this.#anchors.push(point);
    this.#liveTrace = [];
  }

  #close(): void {
    const { store } = this.#deps;
    const document = store.document;
    if (!this.active || document === null) return;
    // 闭合前把当前指针到最后一个紧固点的实时预览段也并进去，回到起点的
    // 那一小段路径也贴边，不是直接连一条直线收尾
    const points = this.previewPath();
    this.#reset();
    if (points.length < 3) return;
    const { width, height } = document.size;
    store.setSelection(applyCombine(store.selection, lassoMask(width, height, points), this.#combine));
    this.#deps.requestRender();
  }

  #reset(): void {
    this.#image = null;
    this.#anchors = [];
    this.#committed = [];
    this.#liveTrace = [];
  }
}
